// src/hooks/useLegalQueries.js
// React Query hooks for the Legal/Advocate case flow.
// Mirrors useWorkshopQueries.js conventions — same axios instance, same
// KEYS pattern, same response envelope ({ message, data }).

import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import api from "../axiosInstance";

export const LEGAL_KEYS = {
  cases: (filters) => ["legal-cases", filters],
  stats: () => ["legal-cases-stats"],
  caseDetail: (caseNo) => ["legal-case", caseNo],
  timeline: (caseNo) => ["legal-case-timeline", caseNo],
};

export function useLegalCaseStats(options = {}) {
  return useQuery({
    queryKey: LEGAL_KEYS.stats(),
    queryFn: async () => {
      const { data } = await api.get("/api/admin/legal-cases/stats");
      const stats = data?.data || {};
      return {
        allCases: stats.allCases ?? 0,
        awaitingAssignment: stats.awaitingAssignment ?? 0,
        inProgress: stats.inProgress ?? 0,
        completedToday: stats.completedToday ?? 0,
      };
    },
    enabled: options.enabled ?? true,
    refetchInterval: 60000,
  });
}

function parseJsonObject(value) {
  if (!value) return {};
  if (typeof value === "object") return value;

  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

// ─── Normalize a raw legal_cases row from the backend ────────────────────────
function normalizeLegalCaseRow(c) {
  return {
    id: c.id,
    caseNo: c.caseNo,
    vehicleNumber: c.vehicleNumber || null,
    customerName: c.customerName || null,
    customerPhone: c.customerPhone || null,
    customerEmail: c.customerEmail || null,
    incidentLocation: c.incidentLocation || null,
    incidentLat: c.incidentLat || null,
    incidentLng: c.incidentLng || null,
    accidentDate: c.accidentDate || null,
    accidentTime: c.accidentTime || null,
    caseType: c.caseType || null,
    caseDescription: c.caseDescription || null,
    authorityType: c.authorityType || "POLICE_STATION",
    authorityName: c.authorityName || null,
    firNumber: c.firNumber || null,
    firCopyUrl: c.firCopyUrl || null,
    injuryReported: !!c.injuryReported,
    deathReported: !!c.deathReported,
    priority: c.priority || "MEDIUM",
    adminNotes: c.adminNotes || null,
    caseStep: c.caseStep,
    directlyClosed: !!c.directlyClosed,
    bailPath: c.bailPath || null,
    advocateId: c.advocateId || null,
    advocateName: c.advocateName || null,
    advocateNumber: c.advocateNumber || null,
    companyRep: c.companyRepName
      ? {
          name: c.companyRepName,
          fatherName: c.companyRepFatherName,
          aadhar: c.companyRepAadhar,
          aadharCardUrl: c.companyRepAadharCardUrl || null,
          pan: c.companyRepPan,
          panCardUrl: c.companyRepPanCardUrl || null,
          mobile: c.companyRepMobile,
          email: c.companyRepEmail,
          designation: c.companyRepDesignation,
        }
      : null,
    poa:
      c.poaUrl || c.poaGeneratedAt
        ? { fileUrl: c.poaUrl, generatedAt: c.poaGeneratedAt }
        : null,
    driverBail: parseJsonObject(c.driverBailJson),
    vehicleBail: parseJsonObject(c.vehicleBailJson),
    createdBy: c.createdBy || null,
    accidentCaseNo: c.accidentCaseNo || null,
    createdAt: c.createdAt,
    updatedAt: c.updatedAt,
  };
}

// ─── List ─────────────────────────────────────────────────────────────────────
export function useLegalCases(filters = {}, options = {}) {
  return useInfiniteQuery({
    queryKey: LEGAL_KEYS.cases(filters),
    enabled: options.enabled ?? true,
    initialPageParam: null,
    queryFn: async ({ pageParam = null }) => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
      if (pageParam) params.append("cursor", pageParam);
      params.append("limit", "20");

      const { data } = await api.get(`/api/admin/legal-cases?${params}`);
      const raw = data?.data || [];

      return {
        cases: raw.map(normalizeLegalCaseRow),
        nextCursor: data?.nextCursor || null,
        total: data?.total ?? raw.length,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: 0,
  });
}

// ─── Detail ───────────────────────────────────────────────────────────────────
export function useLegalCaseDetail(caseNo) {
  return useQuery({
    queryKey: LEGAL_KEYS.caseDetail(caseNo),
    queryFn: async () => {
      const { data } = await api.get(`/api/admin/legal-cases/${caseNo}`);
      const raw = data?.data || {};
      const c = raw.case || {};

      const documents = (raw.documents || []).map((d) => ({
        id: d.id,
        type: d.documentType, // CHANGED
        fileUrl: d.fileUrl,
        status: d.verificationStatus, // CHANGED
        uploadedBy: d.uploadedBy,
        uploadedAt: d.uploadedAt,
        remarks: d.remarks || null, // note: accident_documents has no remarks column — this will always be null, harmless but dead
        ulipVerified: d.ulipVerified === 1 || d.ulipVerified === true,
        ulipStatus: d.ulipStatus || null,
        ulipExpiryDate: d.ulipExpiryDate || null,
        ulipCheckedAt: d.ulipCheckedAt || null,
      }));

      const accidentMedia = (raw.accidentMedia || []).map((m) => ({
        name: m.name,
        url: m.url,
      }));

      const timeline = (raw.timeline || []).map((t) => ({
        id: t.id,
        status: t.eventType,
        description: t.description,
        role: t.updatedByRole,
        actor: t.updatedById || t.updatedByRole,
        timestamp: t.createdAt,
      }));

      return {
        ...normalizeLegalCaseRow(c),
        documents,
        accidentMedia,
        timeline,
      };
    },
    enabled: !!caseNo,
    refetchOnWindowFocus: true,
  });
}

// ─── Create (Step 1) — multipart: firCopy + accidentMedia[] ─────────────────
export function useCreateLegalCase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (form) => {
      const fd = new FormData();

      const textFields = [
        "vehicleNumber",
        "customerName",
        "customerPhone",
        "customerEmail",
        "incidentLocation",
        "incidentLat",
        "incidentLng",
        "accidentDate",
        "accidentTime",
        "caseType",
        "caseDescription",
        "authorityType",
        "authorityName",
        "firNumber",
        "injuryReported",
        "deathReported",
        "priority",
        "adminNotes",
        "accidentCaseNo",
      ];
      textFields.forEach((key) => {
        if (form[key] !== undefined && form[key] !== null)
          fd.append(key, form[key]);
      });
       if (form.allowDuplicate) fd.append("allowDuplicate", "1");

      if (form.firCopyFile) fd.append("firCopy", form.firCopyFile);
      (form.accidentMedia || []).forEach((file) =>
        fd.append("accidentMedia", file),
      );

      // No Content-Type header — axios sets multipart boundary automatically
      const { data } = await api.post("/api/admin/legal-cases", fd);
      return data; // { message, caseId, caseNo, data }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["legal-cases"] });
      qc.invalidateQueries({ queryKey: ["case"] });
    },
  });
}

// ─── Update Step 1 fields ─────────────────────────────────────────────────────
export function useUpdateLegalCaseInfo(caseNo) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (fields) => {
      const { data } = await api.put(
        `/api/admin/legal-cases/${caseNo}/info`,
        fields,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LEGAL_KEYS.caseDetail(caseNo) });
      qc.invalidateQueries({ queryKey: ["legal-cases"] });
    },
  });
}

// ─── Generic step advance (bail flow, steps 5–11) ────────────────────────────
export function useUpdateLegalCaseStatus(caseNo) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      caseStep,
      bailPath,
      description,
      driverBail,
      vehicleBail,
      ...rest
    }) => {
      const { data } = await api.patch(
        `/api/admin/legal-cases/${caseNo}/status`,
        {
          caseStep,
          bailPath,
          description,
          driverBail,
          vehicleBail,
          ...rest,
        },
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LEGAL_KEYS.caseDetail(caseNo) });
      qc.invalidateQueries({ queryKey: LEGAL_KEYS.timeline(caseNo) });
      qc.invalidateQueries({ queryKey: ["legal-cases"] });
    },
  });
}

// ─── Documents (Step 2) ───────────────────────────────────────────────────────
export function useAddLegalDocument(caseNo) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ type, file }) => {
      const fd = new FormData();
      fd.append("type", type);
      fd.append("document", file);
      const { data } = await api.post(
        `/api/admin/legal-cases/${caseNo}/documents`,
        fd,
      );
      return data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: LEGAL_KEYS.caseDetail(caseNo) }),
  });
}

export function useVerifyLegalDocument(caseNo) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ docId, status, remarks }) => {
      const { data } = await api.patch(
        `/api/admin/legal-cases/${caseNo}/documents/${docId}/verify`,
        { status, remarks },
      );
      return data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: LEGAL_KEYS.caseDetail(caseNo) }),
  });
}

// ─── Advocate assignment (Step 3) ─────────────────────────────────────────────
export function useAssignAdvocateToCase(caseNo) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ advocateId, advocateName, advocateNumber }) => {
      const { data } = await api.patch(
        `/api/admin/legal-cases/${caseNo}/advocate`,
        {
          advocateId,
          advocateName,
          advocateNumber,
        },
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LEGAL_KEYS.caseDetail(caseNo) });
      qc.invalidateQueries({ queryKey: ["legal-cases"] });
    },
  });
}

// ─── POA generation (Step 4) ───────────────────────────────────────────────────
export function useGeneratePOA(caseNo) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (companyRep) => {
      const formData =
        companyRep instanceof FormData
          ? companyRep
          : Object.entries(companyRep || {}).reduce((fd, [key, value]) => {
              if (value !== undefined && value !== null) fd.append(key, value);
              return fd;
            }, new FormData());
      const { data } = await api.post(
        `/api/admin/legal-cases/${caseNo}/poa`,
        formData,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LEGAL_KEYS.caseDetail(caseNo) });
      qc.invalidateQueries({ queryKey: ["legal-cases"] });
    },
  });
}

// ─── Bail-flow document uploads (Steps 7–11) ─────────────────────────────────
export function useUploadBailDocument(caseNo) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ docKey, file }) => {
      const fd = new FormData();
      fd.append("docKey", docKey);
      fd.append("file", file);
      const { data } = await api.post(
        `/api/admin/legal-cases/${caseNo}/bail-document`,
        fd,
      );
      return data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: LEGAL_KEYS.caseDetail(caseNo) }),
  });
}

// ─── Timeline ─────────────────────────────────────────────────────────────────
export function useLegalCaseTimeline(caseNo) {
  return useQuery({
    queryKey: LEGAL_KEYS.timeline(caseNo),
    queryFn: async () => {
      const { data } = await api.get(
        `/api/admin/legal-cases/${caseNo}/timeline`,
      );
      return data?.data || [];
    },
    enabled: !!caseNo,
  });
}
